import { GridModule, ExcelExportService, ToolbarService, FilterService, GridComponent, ExcelExportProperties, GridModel, DetailRowService } from '@syncfusion/ej2-angular-grids';
import { Component, AfterViewInit, ViewChildren, QueryList, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Workbook } from '@syncfusion/ej2-excel-export';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { DmtUtilityService } from '../../../../../../services/dashboard/dmt-utility.service';

@Component({
  standalone: true,
  selector: 'app-generate-excel',
  imports: [CommonModule, GridModule],
  providers: [ExcelExportService, ToolbarService, FilterService, DetailRowService],
  templateUrl: './generate-excel.component.html',
  styleUrl: './generate-excel.component.css',
})
export class GenerateExcelComponent implements OnInit, AfterViewInit {

  @ViewChildren('monthGrid') grids!: QueryList<GridComponent>;
  @Output() sendEndMessage = new EventEmitter();
  @Input() widgetName: string = "";
  sumAccessForecast: number = 0;
  sumAccessActual: number = 0;

  sumMarketForecast: number = 0;
  sumMarketActual: number = 0;

  openToAccessInfo: any;
  openToMarketInfo: any;
  openToEconomiaInfo: any;

  medicineId: number = 0;
  countryId: number = 0;

  // stores flattened data structure for export with regions and hospitals (curva di mercato)
  dataForExport: any[] = [];

  // ===== COMMON FUNCTIONALITY FOR BOTH CURVES =====

  // formats numeric values as percentages with 2 decimal places
  // ===== COMMON FUNCTIONALITY FOR BOTH CURVES =====

  // formats numeric values as percentages with 2 decimal places
  formatPercent = (field: string, data: any, column: any) => {
    if (data.regionName === ' ') return ''; // for empty string
    const value = data[field]?.toFixed(2) + '%' || '';
    return value.replace('.', ',');
  };

  fixExcelFormat (args: any): void {
    if (args.column.field === 'forecast' || args.column.field === 'actual') {
      const originalValue = args.data[args.column.field];

      if (originalValue !== undefined && originalValue !== null) {
        args.value = originalValue;
        args.style = {
          ...args.style,
          format: '0.00',
          hAlign: 'Left'
        };
      }
    }
  }

  constructor (private dataService: DataService, private us: DmtUtilityService) { }

  // initializes component and prepares data based on widget type
  ngOnInit (): void {
    this.getSessionVariables();

    if (this.widgetName === 'widget-curva-accesso') {
      this.dataService.getOpenToAccessReport(this.medicineId, this.countryId).subscribe((data) => {
        this.openToAccessInfo = data;

        // total for curva di accesso
        for (let i = 0; i < this.openToAccessInfo.length; i++) {
          let monthTotalForecast = 0;
          let monthTotalActual = 0;

          for (let j = 0; j < this.openToAccessInfo[i].regions.length; j++) {
            monthTotalForecast += this.openToAccessInfo[i].regions[j].forecast;
            monthTotalActual += this.openToAccessInfo[i].regions[j].actual;
          }


          // add empty row and then total row, the value is 0 
          // but it won't show because in formatPercent if (data.regionName === ' ') return '';
          this.openToAccessInfo[i].regions.push({
            regionName: ' ',
            forecast: 0,
            actual: 0
          });

          this.openToAccessInfo[i].regions.push({
            regionName: 'TOTALE',
            forecast: monthTotalForecast,
            actual: monthTotalActual,
          });
        }
      });

    } else if (this.widgetName === 'widget-curva-mercato') {
      this.dataService.getOpenToMarketReport(this.medicineId, this.countryId).subscribe((data => {
        this.openToMarketInfo = data;
        // prepares data structure for market curve export (curva di mercato)
        this.prepareDataForExport(this.openToMarketInfo);
      }));
    } else if (this.widgetName === 'widget-curva-economia') {
      this.dataService.getOpenToEconomiaReport(this.medicineId, this.countryId).subscribe((data => {
        this.openToEconomiaInfo = data;
        // reuses the same flattened region/hospital structure as the market curve export
        this.prepareDataForExport(this.openToEconomiaInfo);
      }));
    }

  }

  getSessionVariables () {
    this.medicineId = this.us.getSessionVariableNumber('medicineId', 0);
    this.countryId = this.us.getSessionVariableNumber('countryId');
  }

  // waits for view to initialize then starts export process
  ngAfterViewInit (): void {
    setTimeout(() => this.exportAllGrids(), 1500);
  }

  // ===== MARKET CURVE SPECIFIC FUNCTIONALITY =====

  // transforms hierarchical market/economia data into flat structure for excel export (curva di mercato / curva di economia)
  private prepareDataForExport (sourceInfo: any): void {
    this.dataForExport = sourceInfo.map((monthData: any) => {
      const allRowsForExport: any[] = [];
      let monthTotalForecast = 0;
      let monthTotalActual = 0;

      monthData.regions.forEach((region: any) => {
        // calculate total (only regions, not hospitals)
        monthTotalForecast += region.forecast;
        monthTotalActual += region.actual;

        // adds region row with identification flag (curva di mercato)
        allRowsForExport.push({
          ...region,
          isRegion: true
        });

        // adds hospitals header row (curva di mercato)
        allRowsForExport.push({
          regionName: 'Ospedali:',
          isHospitalTitle: true,
          forecast: '',
          actual: ''
        });

        // adds all hospitals for this region with indentation (curva di mercato)
        region.hospitals.forEach((hospital: any) => {
          allRowsForExport.push({
            ...hospital,
            isHospital: true,
            regionName: '   ' + hospital.hospitalOneKey + ' - ' + hospital.hospitalName
          });
        });

        // add two empty rows as separator between regions (curva di mercato)
        allRowsForExport.push({
          regionName: '',
          isEmptyRow: true,
          forecast: '',
          actual: ''
        });
      });

      allRowsForExport.push({
        regionName: ' ',
        isEmptyRow: true,
        forecast: '',
        actual: ''
      });

      // add the Total row at the end
      allRowsForExport.push({
        regionName: 'TOTALE REGIONI',
        forecast: monthTotalForecast,
        actual: monthTotalActual,
        isTotal: true,
      });

      return {
        ...monthData,
        allRowsForExport: allRowsForExport
      };
    });
  }

  // custom value formatter for different row types in market curve (curva di mercato)
  public customValueAccessor = (field: string, data: any, column: any) => {
    if (field === 'regionName') {
      if (data.isHospitalTitle) {
        // makes "Ospedali" text smaller and elegant using unicode (curva di mercato)
        return 'Ospedali ▾';
      }
      return data[field];
    }

    // returns empty string for header and spacer rows (curva di mercato)
    if (data.isHospitalTitle || data.isEmptyRow) {
      return '';
    }

    return this.formatPercent(field, data, column);
  };


  // ===== COMMON EXPORT FUNCTIONALITY =====

  // exports all grids to excel with multiple sheets
  async exportAllGrids (): Promise<void> {
    const gridsArray = this.grids.toArray();

    // configures excel export with multiple sheets
    const exportSettings: ExcelExportProperties = {
      multipleExport: { type: 'NewSheet' },
    };

    // disables hierarchy export for market/economia curves since we use flat structure (curva di mercato / curva di economia)
    if (this.widgetName === 'widget-curva-mercato' || this.widgetName === 'widget-curva-economia') {
      exportSettings.hierarchyExportMode = 'None';
    }

    let workbook: any = null;

    // processes each grid sequentially
    for (let i = 0; i < gridsArray.length; i++) {

      // uses prepared flat data for market/economia curve grids (curva di mercato / curva di economia)
      if ((this.widgetName === 'widget-curva-mercato' || this.widgetName === 'widget-curva-economia') && this.dataForExport[i]) {
        gridsArray[i].dataSource = this.dataForExport[i].allRowsForExport;
      }

      // exports first grid or appends to existing workbook
      if (i === 0) {
        workbook = await gridsArray[i].excelExport(exportSettings, true);
      } else {
        workbook = await gridsArray[i].excelExport(exportSettings, true, workbook);
      }

      // renames worksheet based on month data
      if (workbook && workbook.worksheets[i]) {
        const monthData = this.getDataForCurrentWidget()[i];
        workbook.worksheets[i].name = this.formatMonthName(monthData.month);
      }
    }

    // saves the final workbook as excel file
    if (workbook) {
      const book = new Workbook(workbook, 'xlsx');
      const fileName = this.widgetName === 'widget-curva-mercato'
        ? 'Report_Curva_di_Mercato_Ospedaliero.xlsx'
        : this.widgetName === 'widget-curva-economia'
          ? 'Report_Curva_di_Apertura_in_Economia.xlsx'
          : 'Report_Curva_di_Accesso_Regionale.xlsx';

      book.save(fileName);
      // notifies parent component that export is complete
      this.sendEndMessage.emit();
    }
  }

  // returns appropriate dataset based on widget type
  private getDataForCurrentWidget (): any[] {
    switch (this.widgetName) {
      case 'widget-curva-mercato':
        return this.openToMarketInfo;
      case 'widget-curva-economia':
        return this.openToEconomiaInfo;
      default:
        return this.openToAccessInfo;
    }
  }

  // converts month code (YYYYMM) to formatted month name and year
  private formatMonthName (code: string): string {
    if (!code || code.length !== 6) return code;
    const year = code.slice(0, 4);
    const month = code.slice(4);

    const monthNames: { [key: string]: string } = {
      '01': 'Gennaio', '02': 'Febbraio', '03': 'Marzo', '04': 'Aprile',
      '05': 'Maggio', '06': 'Giugno', '07': 'Luglio', '08': 'Agosto',
      '09': 'Settembre', '10': 'Ottobre', '11': 'Novembre', '12': 'Dicembre'
    };

    return `${monthNames[month] || month} ${year}`;
  }

  public onExcelQueryCellInfo (args: any): void {
    if (args.data && args.data.isTotal) {
      args.style = {
        backColor: '#fffacd',
        bold: true
      };
    }

    if (args.column.field === 'forecast' || args.column.field === 'actual') {
      const originalValue = args.data[args.column.field];

      if (originalValue !== undefined && originalValue !== null) {
        if (!args.data.isHospitalTitle && !args.data.isEmptyRow) {
          args.value = originalValue;

          args.style = {
            ...args.style,
            format: '0.00',
            hAlign: 'Left'
          };
        }
      }
    }
  }
}